import { useMemo } from 'react';
import { toLabelledEntities } from 'helpers/misc';
import { IssueChartFilterKind } from 'shared/domain/analytics/issueChartFilters';
import type { ProcessType } from 'shared/domain/process/types/model';
import { useWorktypes } from 'store/fieldValue';
import { useProcesses } from 'store/process';
import { useSites } from 'store/site';
import { useAssignees, useImpactFilterOptions, useSubcontractors } from '../hooks';
import type { IssueChartFilterLabelSources, IssueChartFilterOptions } from './types';

/** What each kind of issue chart filter offers, `processes` being the chart's ones. */
export function useIssueChartFilterOptions(processes: ProcessType[]): IssueChartFilterOptions {
  const subcontractors = useSubcontractors();
  const assignees = useAssignees();
  const impacts = useImpactFilterOptions();
  const { items: sites } = useSites();
  const { items: workTypes } = useWorktypes();

  return useMemo(
    () => ({
      [IssueChartFilterKind.originators]: subcontractors,
      [IssueChartFilterKind.process]: toLabelledEntities(processes),
      [IssueChartFilterKind.sites]: sites,
      [IssueChartFilterKind.workType]: workTypes,
      [IssueChartFilterKind.impact]: impacts,
      [IssueChartFilterKind.assignee]: assignees,
    }),
    [subcontractors, processes, sites, workTypes, impacts, assignees]
  );
}

/** Every process of the user names a chosen process, also one this chart does not offer. */
export function useIssueChartFilterLabelSources(): IssueChartFilterLabelSources {
  const { processes } = useProcesses();
  return useMemo(() => ({ [IssueChartFilterKind.process]: toLabelledEntities(processes) }), [processes]);
}
